import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

/**
 * TCN split-screen login — faithful reproduction of the "TCN Login Desktop" +
 * "TCN Login I18n" design bundle (claude.ai/design), wired to real Supabase
 * auth. Rendered only in the TCN context (tcn.noirock.app). Responsive: split
 * panel on desktop, single column on mobile. Language pill is driven by the
 * tenant's enabled_languages (passed as `languages`).
 *
 * Props:
 *   brandName  - tenant display name (e.g. "TCN")
 *   languages  - allowed language codes (subset of en/fr/ar...) for the pill
 *   onSuccess  - (profile) => void, called after successful auth (routing)
 */

// Design tokens (TCN subset of tokens.js)
const T = {
  bg: '#0a0a0b',
  cardAlt: '#1f1f23',
  fg1: '#f5f5f7',
  fg2: '#a8a8ad',
  fg3: '#6e6e73',
  fg4: '#48484c',
  cyan: '#22d3ee',
  amber: '#f5a623',
  red: '#ff2d6f',
  borderSoft: 'rgba(255,255,255,0.06)',
  borderMed: 'rgba(255,255,255,0.10)',
  fontBody: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
  fontDisplay: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
  fontMono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
};

// Editorial + form copy (from the design's LOGIN_I18N, stats removed).
// Falls back to EN when a tenant language has no entry here.
const COPY = {
  en: {
    dir: 'ltr', tagline: 'Creator network · operations', managerEdition: 'Manager edition',
    headlinePre: 'Run your network', headlineMid: 'from ', headlineAccent: 'one console', headlinePost: '.',
    description: 'Track your agents, creators, targets and critical actions from one interface. Synced with the Agent app in real time.',
    welcome: 'Welcome back', title: 'Sign in', subtitle: 'Use your agent credentials',
    email: 'Email', emailPh: 'you@example.com', password: 'Password', passwordPh: '••••••••',
    show: 'Show', hide: 'Hide', remember: 'Remember me', forgot: 'Forgot password?',
    signIn: 'Sign in', signingIn: 'Signing in…', newTo: 'New to TCN?', request: 'Request access',
    support: 'Support', privacy: 'Privacy', terms: 'Terms',
    allRights: 'All rights reserved',
    errEmail: 'Email is required.', errPassword: 'Password is required.',
    errInvalid: 'Invalid credentials. Please try again.',
    errProfile: 'User profile not found. Please contact your administrator.',
    errNetwork: 'Network error. Please check your connection and try again.',
  },
  fr: {
    dir: 'ltr', tagline: 'Réseau de creators · opérations', managerEdition: 'Version manager',
    headlinePre: 'Pilotez votre réseau', headlineMid: 'depuis ', headlineAccent: 'un seul tableau', headlinePost: '.',
    description: "Suivez vos agents, vos creators, vos objectifs et les actions critiques depuis une interface unique. Synchronisé avec l'app Agent en temps réel.",
    welcome: 'Bon retour', title: 'Connexion', subtitle: 'Utilisez vos identifiants agent',
    email: 'E-mail', emailPh: 'vous@example.com', password: 'Mot de passe', passwordPh: '••••••••',
    show: 'Afficher', hide: 'Masquer', remember: 'Se souvenir de moi', forgot: 'Mot de passe oublié ?',
    signIn: 'Se connecter', signingIn: 'Connexion…', newTo: 'Nouveau sur TCN ?', request: 'Demander un accès',
    support: 'Support', privacy: 'Confidentialité', terms: 'Conditions',
    allRights: 'Tous droits réservés',
    errEmail: "L'e-mail est requis.", errPassword: 'Le mot de passe est requis.',
    errInvalid: 'Identifiants invalides. Veuillez réessayer.',
    errProfile: "Profil introuvable. Veuillez contacter votre administrateur.",
    errNetwork: 'Erreur réseau. Vérifiez votre connexion et réessayez.',
  },
  ar: {
    dir: 'rtl', tagline: 'شبكة المبدعين · العمليات', managerEdition: 'نسخة المدير',
    headlinePre: 'أدِر شبكتك', headlineMid: 'من ', headlineAccent: 'وحدة تحكم واحدة', headlinePost: '.',
    description: 'تتبّع وكلاءك ومبدعيك وأهدافك والإجراءات الحرجة من واجهة واحدة. متزامن مع تطبيق الوكيل في الوقت الفعلي.',
    welcome: 'مرحبًا بعودتك', title: 'تسجيل الدخول', subtitle: 'استخدم بيانات اعتماد الوكيل',
    email: 'البريد الإلكتروني', emailPh: 'you@example.com', password: 'كلمة المرور', passwordPh: '••••••••',
    show: 'إظهار', hide: 'إخفاء', remember: 'تذكرني', forgot: 'هل نسيت كلمة المرور؟',
    signIn: 'تسجيل الدخول', signingIn: 'جارٍ تسجيل الدخول…', newTo: 'جديد على TCN؟', request: 'طلب الوصول',
    support: 'الدعم', privacy: 'الخصوصية', terms: 'الشروط',
    allRights: 'جميع الحقوق محفوظة',
    errEmail: 'البريد الإلكتروني مطلوب.', errPassword: 'كلمة المرور مطلوبة.',
    errInvalid: 'بيانات الاعتماد غير صحيحة. يرجى المحاولة مرة أخرى.',
    errProfile: 'لم يتم العثور على ملف المستخدم. يرجى التواصل مع المسؤول.',
    errNetwork: 'خطأ في الشبكة. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
  },
};

function useIsDesktop() {
  const q = '(min-width: 1024px)';
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(q).matches : true));
  useEffect(() => {
    const m = window.matchMedia(q);
    const h = (e) => setD(e.matches);
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, []);
  return d;
}

function GlobeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={T.fg3} strokeWidth="1.4" style={{ marginInlineStart: 8, marginInlineEnd: 4 }}>
      <circle cx="7" cy="7" r="5.5" />
      <path d="M1.5 7h11" />
      <path d="M7 1.5c1.5 1.5 2.3 3.4 2.3 5.5S8.5 11 7 12.5" />
      <path d="M7 1.5C5.5 3 4.7 5 4.7 7s.8 4 2.3 5.5" />
    </svg>
  );
}

function LangPill({ value, options, onChange }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.borderSoft}`,
      borderRadius: 999, padding: 4, direction: 'ltr',
    }}>
      <GlobeIcon />
      {options.map((code) => (
        <button key={code} type="button" onClick={() => onChange(code)} style={{
          background: value === code ? T.fg1 : 'transparent',
          color: value === code ? T.bg : T.fg2,
          border: 'none', padding: '6px 12px', borderRadius: 999,
          font: `600 11px/1 ${T.fontBody}`, letterSpacing: '0.06em', cursor: 'pointer',
          transition: 'all 180ms ease',
        }}>{code.toUpperCase()}</button>
      ))}
    </div>
  );
}

function Field({ label, type, value, onChange, placeholder, autoComplete, dir, trailing, onTrailing }) {
  const [focused, setFocused] = useState(false);
  const rtl = dir === 'rtl';
  return (
    <div style={{
      background: T.cardAlt,
      border: `1px solid ${focused ? 'rgba(34,211,238,0.40)' : T.borderSoft}`,
      borderRadius: 12, padding: '12px 16px', transition: 'all 180ms ease', direction: dir,
      boxShadow: focused ? '0 0 0 4px rgba(34,211,238,0.10)' : 'none',
    }}>
      <div style={{
        font: `600 10px/1 ${T.fontBody}`, color: focused ? T.cyan : T.fg3,
        letterSpacing: '0.10em', textTransform: rtl ? 'none' : 'uppercase', transition: 'color 180ms ease',
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          dir={rtl && type !== 'email' ? 'rtl' : 'ltr'}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, border: 'none', background: 'transparent', color: T.fg1, outline: 'none',
            font: `500 15px/1.2 ${T.fontBody}`, letterSpacing: '-0.01em', padding: 0,
            textAlign: rtl ? 'right' : 'left',
          }}
        />
        {trailing && (
          <button type="button" onClick={onTrailing} style={{
            background: 'none', border: 'none', color: T.fg3,
            font: `500 12px/1 ${T.fontBody}`, cursor: 'pointer', flexShrink: 0,
          }}>{trailing}</button>
        )}
      </div>
    </div>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} style={{
      width: 18, height: 18, borderRadius: 5,
      border: `1.5px solid ${checked ? T.cyan : T.borderMed}`,
      background: checked ? T.cyan : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      transition: 'all 150ms ease', cursor: 'pointer',
    }}>
      {checked && (
        <svg width="11" height="9" viewBox="0 0 10 8" fill="none">
          <path d="M1 4l2.5 2.5L9 1" stroke="#0a0a0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

function Arrow({ rtl }) {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" style={{ transform: rtl ? 'scaleX(-1)' : 'none' }}>
      <path d="M2 7h14M11 2l5 5-5 5" />
    </svg>
  );
}

export default function TcnLoginDesktop({ brandName = 'TCN', languages = ['en', 'ar'], onSuccess }) {
  const { i18n } = useTranslation();
  const isDesktop = useIsDesktop();

  const options = (languages?.length ? languages : ['en']).filter((c) => COPY[c] || ['it', 'de'].includes(c));
  const baseLang = (i18n.language || 'en').split('-')[0];
  const lang = options.includes(baseLang) ? baseLang : options[0];
  const L = COPY[lang] || COPY.en;
  const isRTL = L.dir === 'rtl';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pickLang = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email.trim()) { setError(L.errEmail); return; }
    if (!password) { setError(L.errPassword); return; }
    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(), password,
      });
      if (authError) { setError(authError.message || L.errInvalid); setLoading(false); return; }
      const userId = authData?.user?.id;
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, full_name, tenant_id, agency_id, language')
        .eq('id', userId)
        .single();
      if (profileError || !profile) {
        setError(L.errProfile);
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }
      onSuccess?.(profile);
    } catch {
      setError(L.errNetwork);
      setLoading(false);
    }
  };

  // ---- Reusable form block (shared by desktop right panel + mobile) ---------
  const formBlock = (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{
        font: `700 11px/1 ${T.fontBody}`, letterSpacing: '0.14em', color: T.cyan,
        textTransform: isRTL ? 'none' : 'uppercase', marginBottom: 14, textAlign: isRTL ? 'right' : 'left',
      }}>{L.welcome}</div>
      <h2 style={{ font: `700 ${isDesktop ? 36 : 28}px/1.1 ${T.fontDisplay}`, letterSpacing: '-0.03em', margin: 0, textAlign: isRTL ? 'right' : 'left' }}>{L.title}</h2>
      <p style={{ font: `15px/1.5 ${T.fontBody}`, color: T.fg2, margin: '10px 0 0', textAlign: isRTL ? 'right' : 'left' }}>{L.subtitle}</p>

      {error && (
        <div style={{
          marginTop: 18, padding: '11px 14px', borderRadius: 10,
          background: 'rgba(255,45,111,0.10)', border: '1px solid rgba(255,45,111,0.30)',
          color: T.red, font: `500 13px/1.4 ${T.fontBody}`, direction: L.dir, textAlign: isRTL ? 'right' : 'left',
        }}>{error}</div>
      )}

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label={L.email} type="email" value={email} onChange={setEmail} placeholder={L.emailPh} autoComplete="email" dir={L.dir} />
        <Field label={L.password} type={showPw ? 'text' : 'password'} value={password} onChange={setPassword}
          placeholder={L.passwordPh} autoComplete="current-password" dir={L.dir}
          trailing={showPw ? L.hide : L.show} onTrailing={() => setShowPw((s) => !s)} />
      </div>

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', direction: L.dir }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.fg2, font: `13px/1 ${T.fontBody}`, cursor: 'pointer' }}>
          <Checkbox checked={remember} onChange={setRemember} />
          {L.remember}
        </label>
        <a href="mailto:contact@noirock.app" style={{ color: T.cyan, font: `13px/1 ${T.fontBody}`, textDecoration: 'none' }}>{L.forgot}</a>
      </div>

      <button type="submit" disabled={loading} style={{
        width: '100%', background: T.cyan, color: T.bg, border: 'none',
        padding: '16px 22px', borderRadius: 12, font: `600 15px/1 ${T.fontBody}`,
        cursor: loading ? 'wait' : 'pointer', letterSpacing: '-0.01em', opacity: loading ? 0.7 : 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        boxShadow: '0 12px 28px -8px rgba(34,211,238,0.40)',
        marginTop: 20,
      }}
        onMouseEnter={(e) => { if (loading) return; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 32px -8px rgba(34,211,238,0.55)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 12px 28px -8px rgba(34,211,238,0.40)'; }}>
        <span>{loading ? L.signingIn : L.signIn}</span>
        {!loading && <Arrow rtl={isRTL} />}
      </button>

      <div style={{ marginTop: 18, textAlign: 'center', font: `13px/1.5 ${T.fontBody}`, color: T.fg3 }}>
        {L.newTo} <a href="mailto:contact@noirock.app" style={{ color: T.cyan, fontWeight: 500, textDecoration: 'none' }}>{L.request}</a>
      </div>
    </form>
  );

  // ---- MOBILE : single column ----------------------------------------------
  if (!isDesktop) {
    return (
      <div style={{
        minHeight: '100dvh', background: T.bg, color: T.fg1, font: `15px/1.4 ${T.fontBody}`,
        letterSpacing: '-0.01em', direction: L.dir, position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ position: 'absolute', top: -120, left: '50%', transform: 'translateX(-50%)', width: 420, height: 420, background: 'radial-gradient(circle, rgba(34,211,238,0.20), rgba(34,211,238,0) 70%)', filter: 'blur(8px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -80, [isRTL ? 'left' : 'right']: -40, width: 260, height: 260, background: 'radial-gradient(circle, rgba(245,166,35,0.14), rgba(245,166,35,0) 70%)', filter: 'blur(12px)', pointerEvents: 'none' }} />

        <div style={{
          position: 'relative', display: 'flex', flexDirection: 'column', flex: 1,
          paddingLeft: 20, paddingRight: 20,
          // safe-area so the language pill / footer clear the iPhone status bar
          // and home indicator in the installed PWA (black-translucent).
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', direction: 'ltr', flexShrink: 0 }}>
            <LangPill value={lang} options={options} onChange={pickLang} />
          </div>
          {/* logo + card centered vertically between the pill (top) and footer (bottom) */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img src="/logo-tcn.png" alt={brandName} width={52} height={52} style={{ width: 52, height: 52, borderRadius: 16, display: 'block', boxShadow: '0 12px 32px -8px rgba(34,211,238,0.50)' }} />
              <div style={{ marginTop: 10, font: `700 17px/1.1 ${T.fontDisplay}`, letterSpacing: '-0.02em' }}>{brandName}</div>
              <div style={{ font: `12px/1.4 ${T.fontBody}`, color: T.fg3, marginTop: 4, textAlign: 'center' }}>{L.tagline}</div>
            </div>
            <div style={{ marginTop: 20, background: 'rgba(22,22,24,0.85)', backdropFilter: 'blur(20px)', border: `1px solid ${T.borderSoft}`, borderRadius: 16, padding: 18 }}>
              {formBlock}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 16, flexShrink: 0, font: `12px/1 ${T.fontBody}`, color: T.fg4, direction: L.dir }}>
            <a href="mailto:contact@noirock.app" style={{ color: 'inherit', textDecoration: 'none' }}>{L.support}</a>
            <a href="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>{L.privacy}</a>
            <a href="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>{L.terms}</a>
          </div>
        </div>
      </div>
    );
  }

  // ---- DESKTOP : split screen ----------------------------------------------
  return (
    <div style={{
      width: '100%', minHeight: '100dvh', background: T.bg, color: T.fg1,
      font: `15px/1.4 ${T.fontBody}`, letterSpacing: '-0.01em', display: 'flex', direction: L.dir, overflow: 'hidden',
    }}>
      {/* LEFT — branding */}
      <div style={{
        flex: '1 1 55%', position: 'relative', padding: '40px 60px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden',
        background: 'linear-gradient(135deg, #0a0a0b 0%, #16161a 50%, #0a0a0b 100%)',
        borderInlineEnd: `1px solid ${T.borderSoft}`,
      }}>
        <div style={{ position: 'absolute', top: '-15%', [isRTL ? 'right' : 'left']: '-10%', width: 720, height: 720, background: 'radial-gradient(circle, rgba(34,211,238,0.18), rgba(34,211,238,0) 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-20%', [isRTL ? 'left' : 'right']: '-15%', width: 580, height: 580, background: 'radial-gradient(circle, rgba(245,166,35,0.12), rgba(245,166,35,0) 65%)', filter: 'blur(24px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo-tcn.png" alt={brandName} width={44} height={44} style={{ width: 44, height: 44, borderRadius: 12, display: 'block', boxShadow: '0 12px 32px -8px rgba(34,211,238,0.45)' }} />
          <div>
            <div style={{ font: `700 18px/1 ${T.fontDisplay}`, letterSpacing: '-0.02em' }}>{brandName}</div>
            <div style={{ font: `12px/1.4 ${T.fontBody}`, color: T.fg3, marginTop: 4 }}>{L.tagline}</div>
          </div>
        </div>

        <div style={{ position: 'relative', maxWidth: 540 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999,
            background: 'rgba(34,211,238,0.10)', border: '1px solid rgba(34,211,238,0.25)', color: T.cyan,
            font: `600 11px/1 ${T.fontBody}`, letterSpacing: '0.12em', textTransform: isRTL ? 'none' : 'uppercase', marginBottom: 24,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: T.cyan, boxShadow: `0 0 12px ${T.cyan}` }} />
            {L.managerEdition}
          </div>
          <h1 style={{ font: `700 52px/1.05 ${T.fontDisplay}`, letterSpacing: '-0.035em', margin: 0, textWrap: 'balance' }}>
            {L.headlinePre}<br />{L.headlineMid}<span style={{ color: T.cyan }}>{L.headlineAccent}</span>{L.headlinePost}
          </h1>
          <p style={{ font: `16px/1.55 ${T.fontBody}`, color: T.fg2, marginTop: 20, maxWidth: 460 }}>{L.description}</p>
        </div>

        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: T.fg4, font: `12px/1 ${T.fontBody}` }}>
          <span>© 2026 {brandName} · {L.allRights}</span>
          <span style={{ direction: 'ltr', fontFamily: T.fontMono }}>v {import.meta.env.VITE_APP_VERSION || '2'}</span>
        </div>
      </div>

      {/* RIGHT — form */}
      <div style={{ flex: '1 1 45%', position: 'relative', padding: '32px 64px', display: 'flex', flexDirection: 'column', background: T.bg, minWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', direction: 'ltr' }}>
          <LangPill value={lang} options={options} onChange={pickLang} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 380, margin: isRTL ? '0 0 0 auto' : '0 auto 0 0', width: '100%' }}>
          {formBlock}
        </div>
        <div style={{ display: 'flex', gap: 24, font: `12px/1 ${T.fontBody}`, color: T.fg4, direction: L.dir }}>
          <a href="mailto:contact@noirock.app" style={{ color: 'inherit', textDecoration: 'none' }}>{L.support}</a>
          <a href="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>{L.privacy}</a>
          <a href="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>{L.terms}</a>
        </div>
      </div>
    </div>
  );
}
